import React, { createContext, useContext, useCallback, useRef, useState } from 'react';

// A promise-based replacement for the native window.confirm(). Mount
// <ConfirmProvider> once near the app root, then in any component:
//
//   const confirm = useConfirm();
//   if (!(await confirm({ title, message, confirmLabel, danger: true }))) return;
//
// It renders one shared, on-brand modal instead of the OS dialog, so every
// confirmation across the app looks the same in light and dark mode.

const ConfirmContext = createContext(() => Promise.resolve(false));

export const useConfirm = () => useContext(ConfirmContext);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { title, message, confirmLabel, cancelLabel, danger }
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => {
    setState({
      title: opts.title || 'Are you sure?',
      message: opts.message || '',
      confirmLabel: opts.confirmLabel || 'Confirm',
      cancelLabel: opts.cancelLabel || 'Cancel',
      danger: opts.danger !== false, // default to the destructive (red) style
    });
    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = useCallback((result) => {
    if (resolver.current) {
      resolver.current(result);
      resolver.current = null;
    }
    setState(null);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
          onClick={() => close(false)}
          onKeyDown={(e) => e.key === 'Escape' && close(false)}
        >
          <div
            className="w-full max-w-sm rounded-lg bg-white shadow-xl dark:bg-gray-800"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="p-5">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {state.title}
              </h3>
              {state.message && (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {state.message}
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-3 dark:border-gray-700">
              <button
                onClick={() => close(false)}
                className="px-3 py-1.5 text-sm font-medium text-gray-600 rounded hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {state.cancelLabel}
              </button>
              <button
                autoFocus
                onClick={() => close(true)}
                className={`px-3 py-1.5 text-sm font-medium text-white rounded ${
                  state.danger
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-primary-600 hover:bg-primary-700'
                }`}
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}
